/**
 * CategoryEditor.tsx — 分类 / 标签 / FAQ 分类编辑弹窗（books 域）
 *
 * 移植自原型 design/Novel-admin/src/dialogs2.jsx 490–550（注册名 category-editor）。
 * kind 区分编辑对象：'cats' 分类 / 'tag' 标签 / 'faq-cat' FAQ 分类；名称多语言（中英必填 + 其他语言可选）。
 *
 * 接真（ADM-BOK-S001/S002）：'cats'/'tag' 的保存走真实写：
 *   - 新建：categoriesApi.create / tagsApi.create（body { slug, name_i18n }；slug 由英文名 slugify 派生，
 *     原型无 slug 录入入口——server SlugSchema 要求 alnum/hyphen，故从 en/zh 名生成，运营无需手填）。
 *   - 编辑：categoriesApi.update / tagsApi.update（body { name_i18n }，slug 创建后不可改，契约 omit）。
 *     编辑需真实 id（editRow.id），由列表行 CatTagRow 传入。
 * 成功 invalidate ['categories'] / ['tags'] 刷新 + onCreated 回填（新建场景）；失败错误 toast 不关弹窗（不假成功）。
 * 'faq-cat'：FAQ 分类是 feedback.category 闭集 enum、无自由分类 CRUD 端点（NOT-in-scope）→ 保存 disabled。
 * 1:1 保真：JSX / className / inline-style 原样搬，window.__multiLang.MultiLangFormRows →
 * ~/ui/MultiLangFormRows 受控版；window.openDialog hub → 受控 open state（由宿主页持有）。
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSysConfig } from '~/data/queries';
import { categoriesApi, tagsApi } from '~/hooks/use-books';
import { Icons } from '~/lib/icons';
import { staleSnapshotErrorMessage } from '~/lib/stale-snapshot-error';
import { useAuthStore } from '~/stores/auth-store';
import { Dialog } from '~/ui/Dialog';
import { MultiLangFormRows, type MultiLangValue } from '~/ui/MultiLangFormRows';
import { toast } from '~/ui/Toast';

// 角色不足时写动作的统一禁用标注（前端 RBAC 防线；后端 requireRole 才是权威）
const FORBIDDEN_ROLE_TITLE = '权限不足 · 该操作受角色限制（后端 requireRole 为权威）';

/** category-editor 唤起项：分类 / 标签 / FAQ 分类（编辑场景携带真实 id）。 */
export interface CategoryEditorItem {
  /** 真实 DTO id（编辑 update 用）；新建场景无 id */
  id?: string;
  name: string;
  nameEn?: string;
  books?: number;
  sort?: number;
  /**
   * A6 §14.2 / §14.4 乐观并发令牌（`CategoryResponse` / `TagResponse` 的 `updated_at`）。
   * 编辑场景必带；FAQ 分类闭集无 CRUD、新建场景无此值。
   */
  updatedAt?: string;
  /**
   * **完整** `name_i18n`。PATCH 是整份替换，编辑面必须以它为初值 —— 只回填 zh/en 的话，
   * 其余语言（自动翻译补齐的那些）会在**每一次**保存时被删掉，与并发无关、乐观锁也拦不住。
   */
  nameI18n?: Record<string, string>;
}

const NOT_IN_SCOPE_TITLE =
  '待契约（NOT-in-scope，见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五）';

/** 由名称派生 slug：取 en（无则 zh）小写、空格/非字母数字转连字符（对齐 server SlugSchema alnum/hyphen）。 */
function deriveSlug(name: MultiLangValue): string {
  const base = (name.en || name.zh || '').trim().toLowerCase();
  const slug = base
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || `cat-${Date.now().toString(36)}`;
}

export function CategoryEditor({
  kind,
  item,
  onClose,
  onCreated,
}: {
  kind: 'cats' | 'tag' | 'faq-cat';
  item?: CategoryEditorItem;
  onClose: () => void;
  onCreated?: (name: string) => void;
}) {
  const isTag = kind === 'tag';
  const isFaqCat = kind === 'faq-cat';
  // 「其他语言」全集 = useSysConfig 真数据 enabled 语言 code（组件默认 fallback 仅 zh/en，不读 mock seed）
  const { data: sys } = useSysConfig();
  const enabledLangs = sys.languages.filter((l) => l.enabled).map((l) => l.language_code);
  const noun = isTag ? '标签' : '分类';
  const usageNoun = isFaqCat ? 'FAQ 问题' : '本书';
  // 名称多语言：以**完整** name_i18n 为初值（缺省回退 zh/en 两键，兼容尚未带 nameI18n 的调用方，
  // 如 FAQ 分类）。不这么做的话，DB 里已有而这里没载入的语言会在保存时被整份替换删掉。
  const [name, setName] = useState<MultiLangValue>(() =>
    item?.nameI18n && Object.keys(item.nameI18n).length > 0
      ? { ...item.nameI18n }
      : { zh: item?.name || '', en: item?.nameEn || item?.name || '' },
  );
  const queryClient = useQueryClient();
  const isEdit = !!item?.id;
  const queryKey = isTag ? 'tags' : 'categories';
  // 弹窗自身 RBAC 门控（会话内可降级，已挂载弹窗要随之禁用 Save）：server POST/PATCH /categories|/tags
  // requireRole('root','admin','operator')；viewer 不可写。后端 requireRole 为权威。
  const role = useAuthStore((s) => s.user?.role);
  const canWrite = role === 'root' || role === 'admin' || role === 'operator';

  // 仅提交非空多语言文案（契约 name_i18n 值 min(1)，空串会被服务端拒绝）
  const buildNameI18n = (): Record<string, string> =>
    Object.fromEntries(
      Object.entries(name)
        .map(([k, v]) => [k, v.trim()] as const)
        .filter(([, v]) => v.length > 0),
    );

  const saveMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const name_i18n = buildNameI18n();
      if (isEdit && item?.id) {
        // 编辑：仅改 name_i18n（slug 创建后不可改，契约 Update 已 omit slug）。
        // `expected_updated_at` 逐字回传载入时的令牌：`name_i18n` 是**全量快照**提交，两个运营各开
        // 一个语言 Tab 时，后保存的一份会把先保存那门语言整片覆盖（A6 §14.2 / §14.4）。
        const patch = {
          name_i18n,
          ...(item.updatedAt ? { expected_updated_at: item.updatedAt } : {}),
        };
        if (isTag) await tagsApi.update(item.id, patch);
        else await categoriesApi.update(item.id, patch);
        return;
      }
      // 新建：slug 由名称派生（原型无 slug 录入入口）；分类 display_order 默认 0（列表整体排序）
      const slug = deriveSlug(name);
      if (isTag) await tagsApi.create({ slug, name_i18n });
      else await categoriesApi.create({ slug, name_i18n, display_order: 0 });
    },
    onSuccess: async () => {
      const display = (name.zh || name.en || item?.name || '').trim();
      toast(`已${isEdit ? '保存' : '创建'}${noun}「${display}」`, {
        tone: 'pos',
        icon: 'IconCheck',
      });
      // await invalidate：刷新完成前 isPending 保持真、Save 不复活（adversarial F4）；完成后再回填 + 关闭
      await queryClient.invalidateQueries({ queryKey: [queryKey] });
      // 新建场景回填到宿主（NewBookDialog 的本地分类/标签草稿）
      if (!isEdit && onCreated) onCreated(display);
      onClose();
    },
    onError: (err) => {
      // 陈旧快照冲突走专用文案：确定性写「本次未保存」+ 引导刷新后重新修改（直接重试会覆盖对方）。
      // 通用「操作冲突，请刷新后重试」在这里是错的 —— 全量快照提交下「重试」正是覆盖动作。
      const stale = staleSnapshotErrorMessage(err, noun);
      toast(stale ?? (err instanceof Error ? err.message : `${noun}保存失败`), {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  // zh/en 必填（契约 I18nFieldsSchema 要求 zh+en 必有）+ 非 FAQ 分类（FAQ 闭集无 CRUD）+ 有写权限 + 非 pending
  const canSubmit =
    !isFaqCat &&
    canWrite &&
    (name.zh ?? '').trim().length > 0 &&
    (name.en ?? '').trim().length > 0 &&
    !saveMutation.isPending;

  return (
    <Dialog
      width={600}
      title={item ? `编辑${noun} · ${item.name}` : `新建${noun}`}
      subtitle={`中英必填 · 其他语言可后续维护 · 未维护时 fallback 英文 · ${isFaqCat ? '用于 FAQ 分组与排序' : isTag ? '用于书籍筛选 / 推荐' : '用于书库筛选 / 搜索 / 推荐'}`}
      icon={isTag ? 'IconTag' : 'IconFolder'}
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>
            取消
          </button>
          {/* FAQ 分类是 feedback.category 闭集 enum，无自由分类 CRUD 端点（NOT-in-scope §五）→ 禁用保存；viewer 无写权限 → 禁用 */}
          <button
            className="btn btn-accent"
            disabled={!canSubmit}
            title={isFaqCat ? NOT_IN_SCOPE_TITLE : !canWrite ? FORBIDDEN_ROLE_TITLE : undefined}
            onClick={() => saveMutation.mutate()}
          >
            保存
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <MultiLangFormRows
          label={`${noun}名`}
          value={name}
          onChange={setName}
          availableLangs={enabledLangs}
          placeholderMap={{ zh: `${noun}名 (中文)`, en: `${noun} name (English)` }}
          placeholderTpl={(zh) => `${noun}名 (${zh})`}
        />
        {isFaqCat && <FaqSortRow defaultSort={item?.sort || 1} />}
        {item && (
          <div
            style={{
              marginTop: 14,
              padding: 10,
              background: 'var(--bg-subtle)',
              borderRadius: 5,
              fontSize: 11.5,
              color: 'var(--fg-muted)',
            }}
          >
            <Icons.IconInfo
              size={11}
              style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--info)' }}
            />
            该 {noun} 当前关联{' '}
            {/* 关联书籍数：cat-tag-adapter 已接真（DTO.book_count 书组去重计数，恒 number；dev-docs #253 A6 收录）；
                item.books 来源列表行，?? '—' 仅历史兼容兜底 */}
            <span className="mono strong">{item.books ?? '—'}</span> {usageNoun} ·
            修改将立即生效到客户端
          </div>
        )}
      </div>
    </Dialog>
  );
}

/** FAQ 分类专属排序行（移植自原型 isFaqCat 分支的 FormRow） */
function FaqSortRow({ defaultSort }: { defaultSort: number }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 14,
        padding: '12px 0',
        borderBottom: '1px solid var(--border)',
        alignItems: 'flex-start',
      }}
    >
      <div style={{ width: 140, flexShrink: 0, paddingTop: 6 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500 }}>排序</div>
        <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 3, lineHeight: 1.45 }}>
          数字越小越靠前
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <input
          className="input mono"
          defaultValue={defaultSort}
          style={{ width: 70, textAlign: 'right' }}
        />
      </div>
    </div>
  );
}
